'use es6';

import { Map as ImmutableMap, Seq } from 'immutable';
import { getProperty, getPropertyLabel, getReferenceLabel, hasReferences } from 'reporting-data/v2/dataset/datasetRecords';
import { COUNT_FIELD } from '../../field/constants';
import { selectAndSortFieldValues } from '../../field/sort';
import { getPropertyFormatter, isFormattableProperty } from '../../formatters/propertyFormatter';
import { applyOrder, getDataKeys } from '../../highcharts/chartUtils';
import { Axis, AxisTypes } from './xyRecords';
import { mapLayerSeriesColor, mapLayerSeriesDetail, mapLayerSeriesSizing, mapLayerSeriesX, mapLayerSeriesXColor, mapLayerSeriesXY, mapLayerSeriesXYColor, mapLayerSeriesY, mapLayerSeriesYColor } from './xySeries';
import { copyOrder, findOverlaps, hasOverlapType, mergeDefinedFields } from './xyUtils';
import { applyOpacity } from './xyUtils';
import I18n from 'I18n';
import { DATASET_INSIGHT_TYPES } from 'reporting-data/tsTypes/datasetInsightTypes';
import { isXCategorical } from './xyUtils';
export const mapLayerCategories = (layer, channel) => {
  const {
    dataset
  } = layer;
  const {
    type,
    field,
    order,
    formattingObject
  } = layer.get(channel);
  if (type !== AxisTypes.category && channel !== 'color') {
    return undefined;
  }
  const maybeFormattableColorProperty = getProperty(dataset, field);
  const categoryProperty = isFormattableProperty(maybeFormattableColorProperty) && formattingObject ? maybeFormattableColorProperty.set('formattingType', formattingObject.get('type')).set('formattingObject', formattingObject) : undefined;
  const categories = getDataKeys(dataset, field).filter(key => key !== undefined).map(key => {
    const propertyUsesReferences = hasReferences(dataset, field, key);
    const name = propertyUsesReferences || !categoryProperty ? getReferenceLabel(dataset, field, key) : getPropertyFormatter(categoryProperty)(key);
    return {
      key,
      name
    };
  });
  return applyOrder(categories, order, ({
    key
  }) => key);
};
export const mapLayerAxis = (layer, channel) => {
  const {
    dataset
  } = layer;
  const {
    type,
    field,
    formattingType,
    formattingObject
  } = layer.get(channel);
  if (!field) {
    return Axis({
      title: '',
      type: type === AxisTypes.scaffold ? type : AxisTypes.hidden
    });
  }
  const title = field === COUNT_FIELD ? 'Count' : getPropertyLabel(dataset, field);
  const hasProperty = !!getProperty(dataset, field);
  const maybeFormattableProperty = hasProperty ? getProperty(dataset, field) : ImmutableMap();
  const property = formattingObject && isFormattableProperty(maybeFormattableProperty) ? maybeFormattableProperty.set('formattingType', formattingType).set('formattingObject', formattingObject) : maybeFormattableProperty;
  const categories = mapLayerCategories(layer, channel);
  return Axis({
    title,
    type,
    categories,
    property
  });
};
const drilldownOnPointEvent = (point, layer, levelOfDetail, debug, onPointClick) => {
  const {
    dataset
  } = layer;
  const payload = {
    point,
    layer,
    dataset,
    levelOfDetail
  };
  debug.event(() => ({
    name: 'onPointClick',
    payload: Seq(payload).map(p => p === null || p === void 0 ? void 0 : p.toJS()).toObject()
  }));
  onPointClick(payload);
};
export const mapSeriesWithEvents = (layers, {
  onPointClick
} = {}, debug, series, setFilterOverlapsModalProps = () => {}) => {
  const raasSeriesMap = series.toMap().mapKeys((_, serie) => serie.get('id'));
  const seriesWithEvents = series.map(serie => {
    const points = serie.get('points');
    return serie.set('events', ImmutableMap({
      click(event) {
        // "raas" point houses data from resolve and gets passed to the
        // drilldown generator. "chart" point is based on highchart's point
        // class and contains the info needed to find overlaps
        const raasPoint = points.get(event.point.index);
        const chartPoint = event.point;
        if (onPointClick) {
          const isChartWithPossibleOverlapPoints = series.length > 1 && hasOverlapType(chartPoint.series.options.type.toUpperCase());
          const overlapPoints = isChartWithPossibleOverlapPoints ? findOverlaps(chartPoint, chartPoint.series.chart.series, raasSeriesMap) : ImmutableMap({});
          if (!overlapPoints.isEmpty()) {
            setFilterOverlapsModalProps({
              originalRaasPoint: raasPoint,
              originalChartPoint: chartPoint,
              chartSeries: chartPoint.series.chart.series,
              raasSeriesMap,
              overlapPoints,
              onConfirm: chosenPoint => {
                drilldownOnPointEvent(chosenPoint, layers.get(chosenPoint.get('layerId')), raasSeriesMap.getIn([chosenPoint.get('seriesId'), 'levelOfDetail']), debug, onPointClick);
              }
            });
          } else {
            drilldownOnPointEvent(raasPoint, layers.get(raasPoint.get('layerId')), serie.get('levelOfDetail'), debug, onPointClick);
          }
        }
      }
    }));
  });
  return seriesWithEvents;
};
export const mapLayerSeries = layer => {
  const {
    x: {
      field: xField
    },
    y: {
      field: yField
    },
    color: {
      field: colorField
    },
    sizing: {
      field: sizingField
    },
    detail: {
      field: detailField
    }
  } = layer;
  let series;
  if (xField && yField && colorField) {
    series = mapLayerSeriesXYColor(layer);
  } else if (xField && yField) {
    series = mapLayerSeriesXY(layer);
  } else if (yField && colorField) {
    series = mapLayerSeriesYColor(layer);
  } else if (yField) {
    series = mapLayerSeriesY(layer);
  } else if (xField && colorField) {
    series = mapLayerSeriesXColor(layer);
  } else if (xField) {
    series = mapLayerSeriesX(layer);
  } else if (colorField) {
    series = mapLayerSeriesColor(layer);
  }

  // scatter only
  if (sizingField) {
    series = mapLayerSeriesSizing(layer, series);
  }
  if (detailField) {
    series = mapLayerSeriesDetail(layer, series);
  }
  if (!series) {
    throw new Error('Invalid encoding configuration, be sure you have at least one linear axis');
  }
  return series;
};
const setChannelOrdering = (layer, channel) => {
  const order = layer.getIn([channel, 'order']);
  const dataset = layer.dataset;
  const field = layer.getIn([channel, 'field']);
  const defined = selectAndSortFieldValues(dataset, field, order);
  return layer.setIn([channel, 'order', 'defined'], defined);
};
export const setLayerOrdering = layer => {
  const {
    x: {
      field: xField,
      type: xType,
      order: xOrder
    },
    y: {
      field: yField,
      type: yType,
      order: yOrder
    },
    color: {
      field: colorField,
      order: colorOrder
    }
  } = layer;
  if (xField && xOrder && xType === AxisTypes.category) {
    layer = setChannelOrdering(layer, 'x');
    if (!colorOrder && colorField === xField) {
      layer = copyOrder(layer, 'x', 'color');
    }
  }
  if (yField && yOrder && yType === AxisTypes.category) {
    layer = setChannelOrdering(layer, 'y');
    if (!colorOrder && colorField === yField) {
      layer = copyOrder(layer, 'y', 'color');
    }
  }
  if (colorField && colorOrder) {
    layer = setChannelOrdering(layer, 'color');
    if (!xOrder && colorField === xField) {
      layer = copyOrder(layer, 'color', 'x');
    }
    if (!yOrder && colorField === yField) {
      layer = copyOrder(layer, 'color', 'y');
    }
  }
  return layer;
};
export const mapLayersToAxes = (layers, channel, style) => {
  const layersByAxis = layers.groupBy((layer, index) => layer.getIn([channel, 'blend']) || String(index));
  const chartAxes = layersByAxis.map((axisLayers, axisId) => {
    const styledAxis = style.getIn([channel, axisId]);
    const primaryLayerAxis = mapLayerAxis(axisLayers.last(), channel);
    return mergeDefinedFields(primaryLayerAxis, styledAxis);
  }).map((axis, id) => axis.set('id', id)).toList();
  return chartAxes.map(axis => Axis(axis));
};
const getSeriesLayer = (series, layers) => {
  return layers.find(layer => layer.get('id') === series.get('layerId'));
};
export const getLayerLinkedToParts = layer => {
  const linkedTo = layer.get('linkedTo');
  const dataset = linkedTo === null || linkedTo === void 0 ? void 0 : linkedTo.get('dataset');
  const metricName = linkedTo === null || linkedTo === void 0 ? void 0 : linkedTo.get('metricName');
  const breakdownValue = linkedTo === null || linkedTo === void 0 ? void 0 : linkedTo.get('breakdownValue');

  // linkedTo not configured on layer, so return original series
  if (!linkedTo || !dataset || !metricName) {
    return undefined;
  }
  const colorCoordinate = linkedTo.get('colorCoordinate');
  const colorOpacity = colorCoordinate && linkedTo.get('colorOpacity');
  const showInLegend = linkedTo.get('showInLegend');
  return {
    dataset,
    metricName,
    breakdownValue,
    colorCoordinate,
    colorOpacity,
    showInLegend
  };
};

/**
 * Finds the linked layer for a given layer.
 *
 * @param   layer  The layer you want to find the linked layer for.
 * @param   allLayers  All layers in the visualization to search for the linked layer.
 * @returns The linked layer, if found. Otherwise, undefined.
 */
export const findLinkedLayer = (layer, allLayers) => {
  const valueFieldPath = isXCategorical(layer) ? ['y', 'field'] : ['x', 'field'];
  const layerLinkedToParts = getLayerLinkedToParts(layer);
  if (!layerLinkedToParts) {
    return undefined;
  }
  const layerColorField = layer.getIn(['color', 'field']);
  const linkedLayer = allLayers.find(otherLayer => {
    const otherLayerDataset = otherLayer.getIn(['dataset', 'key']);
    const otherLayerDatasetValueField = otherLayer.getIn(valueFieldPath);
    const otherLayerDatasetColorField = otherLayer.getIn(['color', 'field']);
    const matchesDataset = otherLayerDataset === layerLinkedToParts.dataset;
    const matchesMetricName = otherLayerDatasetValueField === layerLinkedToParts.metricName;
    const matchesColorFieldIfNeeded = layerColorField ? otherLayerDatasetColorField === layerColorField : true;
    return matchesDataset && matchesMetricName && matchesColorFieldIfNeeded;
  });
  return linkedLayer;
};
const getSeriesLayerLinkedToParts = (series, layers) => {
  const seriesLayer = getSeriesLayer(series, layers);
  return getLayerLinkedToParts(seriesLayer);
};
const findLinkedSeries = (series, allSeries, allLayers) => {
  const seriesLinkedToParts = getSeriesLayerLinkedToParts(series, allLayers);
  if (!seriesLinkedToParts) {
    return undefined;
  }
  return allSeries.find(currentSeries => {
    const currentSeriesLayer = allLayers.find(layer => layer.get('id') === currentSeries.get('layerId'));
    const currentSeriesLayerHasDataset = currentSeriesLayer.getIn(['dataset', 'key']) === seriesLinkedToParts.dataset;
    const currentSeriesMetricName = currentSeries.get('metricName');
    const currentSeriesHasMetricName = currentSeriesMetricName === seriesLinkedToParts.metricName;
    const currentSeriesHasBreakdownValueIfNeeded = !seriesLinkedToParts.breakdownValue || currentSeries.get('breakdownValue') === seriesLinkedToParts.breakdownValue;
    return currentSeriesLayerHasDataset && currentSeriesHasMetricName && currentSeriesHasBreakdownValueIfNeeded;
  });
};
export const setConfiguredLinkedSeries = (series, layers) => {
  return series.reduce((updatedAllSeries, currentSeries, seriesIndex) => {
    const seriesLinkedToParts = getSeriesLayerLinkedToParts(currentSeries, layers);
    if (!seriesLinkedToParts) {
      return updatedAllSeries;
    }
    const seriesToLinkTo = findLinkedSeries(currentSeries, updatedAllSeries, layers);
    if (!seriesToLinkTo) {
      return updatedAllSeries;
    }
    let updatedCurrentSeries = currentSeries.set('linkedTo', seriesToLinkTo.get('id'));
    const seriesToLinkToColor = seriesToLinkTo.getIn(['style', 'color']);
    const shouldCoordinateColor = seriesToLinkToColor && seriesLinkedToParts.colorCoordinate;
    if (shouldCoordinateColor) {
      updatedCurrentSeries = updatedCurrentSeries.setIn(['style', 'color'], applyOpacity(seriesToLinkToColor, seriesLinkedToParts.colorOpacity));
    }
    const shouldShowInLegend = seriesLinkedToParts.showInLegend;
    if (shouldShowInLegend !== undefined) {
      updatedCurrentSeries = updatedCurrentSeries.setIn(['style', 'showInLegend'], shouldShowInLegend);
    }
    return updatedAllSeries.set(seriesIndex, updatedCurrentSeries);
  }, series);
};
export const setSeriesNameForInsightLayers = (series, layers) => {
  return series.map(currentSeries => {
    const seriesLayer = getSeriesLayer(currentSeries, layers);

    // Later we may vary labels for insight types, but using one for all for the time being.
    const seriesLayerInsightType = seriesLayer.get('insightType');
    if (seriesLayerInsightType) {
      const currentSeriesName = currentSeries.get('name');
      let seriesNameTranslationKey = 'reporting-visualizations.insightSeriesName.trend';
      if (seriesLayerInsightType === DATASET_INSIGHT_TYPES.ANOMALY_DETECTION) {
        const seriesLayerType = seriesLayer.getIn(['mark', 'type']);
        seriesNameTranslationKey = seriesLayerType === 'arearange' ? 'reporting-visualizations.insightSeriesName.anomaly.range' : 'reporting-visualizations.insightSeriesName.anomaly.all';
      } else if (seriesLayerInsightType === DATASET_INSIGHT_TYPES.FORECAST_TRENDLINE) {
        seriesNameTranslationKey = 'reporting-visualizations.insightSeriesName.forecast';
      }
      return currentSeries.set('name', I18n.text(seriesNameTranslationKey, {
        chartSeriesName: I18n.SafeString(currentSeriesName)
      }));
    }
    return currentSeries;
  });
};